import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import { useSnackbar } from '@stores/snackbar';
import {
  getReserveErrorMessage,
  getSeatAvailabilityLabel,
  getSeatAvailabilityTier,
  SEAT_AVAILABILITY_TIER
} from '@cont/SeeAll/utils';
import CbtSearchableSelect, { DropdownOption } from './CbtSearchableSelect';

const formatCentreAddress = (centre) => {
  const parts = [
    centre.centreName,
    centre.centreAddress,
    centre.centrePinCode
  ].filter(Boolean);
  return parts.join(', ');
};

export const buildCentreOptions = (centres, selectedCity) => {
  if (!selectedCity) return [];
  return centres.map((centre) => {
    const tier = getSeatAvailabilityTier(
      centre.availableSeats,
      centre.totalSeats
    );
    return {
      label: formatCentreAddress(centre),
      value: centre.centreUid,
      tier,
      availableSeats: centre.availableSeats,
      googleMapsLink: centre.centreGoogleMapsLink,
      disabled: (centre.availableSeats ?? 0) <= 0
    };
  });
};

const CentreDropdownOption = styled(DropdownOption)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacings.SPACING_2B};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const CentreOptionLabel = styled(Typography)`
  font-size: ${Fonts.P1};
  line-height: ${Spacings.SPACING_5B};
  color: var(--color-text-primary);
`;

const SeatTag = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_4B};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $tier }) => {
    if ($tier === SEAT_AVAILABILITY_TIER.LOW) return 'var(--color-i-red-3)';
    if ($tier === SEAT_AVAILABILITY_TIER.MIDDLE)
      return 'var(--color-i-yellow-10)';
    return 'var(--color-i-green-4)';
  }};
  background-color: ${({ $tier }) => {
    if ($tier === SEAT_AVAILABILITY_TIER.LOW) return 'var(--color-i-red-4)';
    if ($tier === SEAT_AVAILABILITY_TIER.MIDDLE)
      return 'var(--color-i-yellow-3)';
    return 'var(--color-i-green-1)';
  }};
`;

const renderCentreDropdownItem = (option, onSelect, onDisabledSelect) => (
  <CentreDropdownOption
    $disabled={option.disabled}
    onClick={option.disabled ? onDisabledSelect : onSelect}
  >
    <CentreOptionLabel variant="p2">{option.label}</CentreOptionLabel>
    <SeatTag $tier={option.tier}>
      {getSeatAvailabilityLabel(option.availableSeats, option.tier)}
    </SeatTag>
  </CentreDropdownOption>
);

const CbtCentreSelect = ({
  options,
  selectedOption,
  onSelect,
  disabled,
  isOpen,
  onToggle
}) => {
  const { open: showSnackbar } = useSnackbar();

  const handleDisabledSelect = () => {
    showSnackbar({
      message: getReserveErrorMessage('no_seats_available'),
      type: 'error'
    });
  };

  const handleRenderDropdownItem = (option, onSelectOption) =>
    renderCentreDropdownItem(option, onSelectOption, handleDisabledSelect);

  return (
    <CbtSearchableSelect
      options={options}
      selectedOption={selectedOption}
      onSelect={onSelect}
      placeholder="Select centre"
      disabled={disabled}
      renderDropdownItem={handleRenderDropdownItem}
      isOpen={isOpen}
      onToggle={onToggle}
    />
  );
};

export default CbtCentreSelect;
